/**
 * Search.
 *
 * The server's query language — `from:`, `in:`, `before:`, `after:`,
 * `has:file` alongside free text — is parsed here as well as there, but only
 * so that it can be *shown*. Every recognised filter becomes a chip the user
 * can see and remove, which turns a syntax you have to already know into one
 * you can discover by typing half of it. The string in the box remains the
 * single source of truth: chips are a view of it, and removing one is an edit
 * to it, so typing and clicking never disagree.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSearch } from '@/api/queries'
import type { Message, Room, SearchHit } from '@/api/types'
import { Avatar, Button, EmptyState, IconButton, Spinner, TextField } from '@/components/ui'
import { formatBytes, formatDay, formatFileKind, formatTime } from '@/lib/format'
import styles from './SearchPanel.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

// ── Query syntax ────────────────────────────────────────────────────────────

export type SearchFilterKind = 'from' | 'in' | 'before' | 'after' | 'has'

export interface SearchFilter {
  kind: SearchFilterKind
  /** Normalised: quotes stripped, and no leading `#` on `in:`. */
  value: string
  /** Offsets into the query the filter was parsed from, so it can be removed. */
  start: number
  end: number
}

export interface ParsedSearchQuery {
  filters: SearchFilter[]
  /** Everything that was not a filter, whitespace collapsed. */
  text: string
}

const FILTER_SOURCE = '(^|\\s)(from|in|before|after|has):("[^"]*"|\\S*)'
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function unquote(value: string): string {
  return value.length >= 2 && value.startsWith('"') && value.endsWith('"')
    ? value.slice(1, -1)
    : value
}

export function parseSearchQuery(query: string): ParsedSearchQuery {
  // Built fresh each call: a shared global regex carries `lastIndex` between
  // calls and would silently skip the front of the next query.
  const pattern = new RegExp(FILTER_SOURCE, 'gi')
  const filters: SearchFilter[] = []

  for (let match = pattern.exec(query); match !== null; match = pattern.exec(query)) {
    const whole = match[0] ?? ''
    const lead = (match[1] ?? '').length
    const kind = (match[2] ?? '').toLowerCase()
    const raw = unquote(match[3] ?? '')

    if (kind !== 'from' && kind !== 'in' && kind !== 'before' && kind !== 'after' && kind !== 'has') {
      continue
    }

    filters.push({
      kind,
      value: kind === 'in' ? raw.replace(/^#/, '') : raw,
      start: match.index + lead,
      end: match.index + whole.length,
    })
  }

  let text = ''
  let cursor = 0
  for (const filter of filters) {
    text += query.slice(cursor, filter.start)
    cursor = filter.end
  }
  text += query.slice(cursor)

  return { filters, text: text.replace(/\s+/g, ' ').trim() }
}

/** Rewrites the query with one filter taken out. */
export function removeSearchFilter(query: string, filter: SearchFilter): string {
  const rewritten = `${query.slice(0, filter.start)} ${query.slice(filter.end)}`
  return rewritten.replace(/\s+/g, ' ').trim()
}

function filterLabel(filter: SearchFilter): string {
  const shown = filter.kind === 'in' ? `#${filter.value}` : filter.value
  return `${filter.kind}: ${shown}`
}

/** Non-null when the filter will not do what the user probably means. */
function filterProblem(filter: SearchFilter): string | null {
  if (filter.value === '') return 'This filter still needs a value.'

  if ((filter.kind === 'before' || filter.kind === 'after') && !DATE_PATTERN.test(filter.value)) {
    return 'Dates are written YYYY-MM-DD.'
  }

  if (filter.kind === 'has' && filter.value.toLowerCase() !== 'file') {
    return 'The only value this server understands is “file”.'
  }

  return null
}

// ── Snippets ────────────────────────────────────────────────────────────────

const SNIPPET_BUDGET = 220
const SNIPPET_LEAD = 60

function searchTerms(text: string): string[] {
  return text
    .split(/\s+/)
    .map((term) => unquote(term).toLowerCase())
    .filter((term) => term.length > 1)
}

interface Snippet {
  text: string
  clippedStart: boolean
  clippedEnd: boolean
}

/** A window of the message around the first term that matched. */
function buildSnippet(body: string, terms: readonly string[]): Snippet {
  const flat = body.replace(/\s+/g, ' ').trim()
  if (flat.length <= SNIPPET_BUDGET) {
    return { text: flat, clippedStart: false, clippedEnd: false }
  }

  const lowered = flat.toLowerCase()
  let at = -1
  for (const term of terms) {
    const found = lowered.indexOf(term)
    if (found !== -1 && (at === -1 || found < at)) at = found
  }

  const from = at === -1 ? 0 : Math.max(0, at - SNIPPET_LEAD)
  const to = Math.min(flat.length, from + SNIPPET_BUDGET)

  return {
    text: flat.slice(from, to).trim(),
    clippedStart: from > 0,
    clippedEnd: to < flat.length,
  }
}

/**
 * Marks each matching term. Built as React elements, never as an HTML string —
 * the message body is written by whoever sent it.
 */
function highlight(text: string, terms: readonly string[]): ReactNode[] {
  if (terms.length === 0) return [text]

  const lowered = text.toLowerCase()
  const nodes: ReactNode[] = []
  let index = 0
  let key = 0

  while (index < text.length) {
    let bestAt = -1
    let bestLength = 0

    for (const term of terms) {
      const at = lowered.indexOf(term, index)
      if (at !== -1 && (bestAt === -1 || at < bestAt)) {
        bestAt = at
        bestLength = term.length
      }
    }

    if (bestAt === -1) {
      nodes.push(text.slice(index))
      break
    }

    if (bestAt > index) nodes.push(text.slice(index, bestAt))
    key += 1
    nodes.push(
      <mark key={`mark-${key}`} className={styles.mark}>
        {text.slice(bestAt, bestAt + bestLength)}
      </mark>,
    )
    index = bestAt + bestLength
  }

  return nodes
}

// ── Presentation helpers ────────────────────────────────────────────────────

function roomLabel(room: Room): string {
  if (room.name !== null && room.name !== '') {
    return room.kind === 'channel' ? `#${room.name}` : room.name
  }
  return room.kind === 'group_direct' ? 'Group message' : 'Direct message'
}

function senderName(message: Message): string {
  const override = message.sender_override
  return override !== null && override !== '' ? override : message.sender.display_name
}

interface ResultGroup {
  room: Room
  hits: SearchHit[]
}

function groupByRoom(hits: readonly SearchHit[]): ResultGroup[] {
  const groups: ResultGroup[] = []
  const byRoom = new Map<string, ResultGroup>()

  for (const hit of hits) {
    const existing = byRoom.get(hit.room.id)
    if (existing === undefined) {
      const created: ResultGroup = { room: hit.room, hits: [hit] }
      byRoom.set(hit.room.id, created)
      groups.push(created)
    } else {
      existing.hits.push(hit)
    }
  }

  return groups
}

function SearchIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <circle cx="7" cy="7" r="4.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.25 10.25 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function FileIcon(): ReactElement {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M11.5 2.75H6.5a1.75 1.75 0 0 0-1.75 1.75v11a1.75 1.75 0 0 0 1.75 1.75h7a1.75 1.75 0 0 0 1.75-1.75V6.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M11.5 2.75V6.5h3.75" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}

function CloseIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

const HINTS: ReadonlyArray<{ token: string; description: string }> = [
  { token: 'from:sondre', description: 'messages from one person' },
  { token: 'in:#relaii-drift', description: 'a single channel' },
  { token: 'has:file', description: 'only messages with attachments' },
  { token: 'after:2026-08-01', description: 'on or after a date' },
  { token: 'before:2026-09-01', description: 'up to a date' },
]

// ── The panel ───────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 250
const PAGE_SIZE = 25
const NO_HITS: readonly SearchHit[] = []

export interface SearchPanelProps {
  /** Seeds the box — `in:#prosjekt-x` to open search scoped to a room. */
  initialQuery?: string
  /**
   * Called instead of navigating. Without it the panel routes to
   * `/rooms/:roomId?message=:messageId`, which the room view uses to scroll
   * the message into place.
   */
  onSelect?: (hit: SearchHit) => void
  autoFocus?: boolean
  className?: string
}

export function SearchPanel({
  initialQuery = '',
  onSelect,
  autoFocus = false,
  className,
}: SearchPanelProps): ReactElement {
  const navigate = useNavigate()

  const [query, setQuery] = useState(initialQuery)
  const [debounced, setDebounced] = useState(initialQuery)

  const inputRef = useRef<HTMLInputElement>(null)
  const resultRefs = useRef<Array<HTMLButtonElement | null>>([])

  // A room can hand the panel a new starting query while it is already open.
  const lastInitial = useRef(initialQuery)
  useEffect(() => {
    if (initialQuery === lastInitial.current) return
    lastInitial.current = initialQuery
    setQuery(initialQuery)
    setDebounced(initialQuery)
  }, [initialQuery])

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (!autoFocus) return
    inputRef.current?.focus()
  }, [autoFocus])

  const parsed = useMemo(() => parseSearchQuery(query), [query])
  const terms = useMemo(() => searchTerms(parseSearchQuery(debounced).text), [debounced])

  const search = useSearch(debounced, { limit: PAGE_SIZE })
  const hits = search.data ?? NO_HITS
  const groups = useMemo(() => groupByRoom(hits), [hits])

  // One flat position per hit, so the arrow keys can walk the whole list
  // without caring which room heading it happens to be under.
  const positioned = useMemo(() => {
    let position = 0
    return groups.map((group) => ({
      room: group.room,
      hits: group.hits.map((hit) => {
        const index = position
        position += 1
        return { hit, index }
      }),
    }))
  }, [groups])

  useEffect(() => {
    resultRefs.current.length = hits.length
  }, [hits.length])

  const focusResult = useCallback((index: number): void => {
    const total = resultRefs.current.length
    if (total === 0) return

    const clamped = index < 0 ? 0 : index >= total ? total - 1 : index
    resultRefs.current[clamped]?.focus()
  }, [])

  const open = useCallback(
    (hit: SearchHit): void => {
      if (onSelect !== undefined) {
        onSelect(hit)
        return
      }
      const params = new URLSearchParams({ message: hit.message.id })
      if (hit.message.parent_id) params.set('thread', hit.message.parent_id)
      navigate(`/rooms/${encodeURIComponent(hit.room.id)}?${params.toString()}`)
    },
    [navigate, onSelect],
  )

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'ArrowDown') return
    event.preventDefault()
    focusResult(0)
  }

  const onResultKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusResult(index + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        if (index === 0) inputRef.current?.focus()
        else focusResult(index - 1)
        break
      case 'Home':
        event.preventDefault()
        focusResult(0)
        break
      case 'End':
        event.preventDefault()
        focusResult(resultRefs.current.length - 1)
        break
      case 'Escape':
        event.preventDefault()
        inputRef.current?.focus()
        break
      default:
        break
    }
  }

  const appendHint = (token: string): void => {
    setQuery((current) => (current === '' ? token : `${current.replace(/\s+$/, '')} ${token}`))
    inputRef.current?.focus()
  }

  const clearQuery = (): void => {
    setQuery('')
    setDebounced('')
    inputRef.current?.focus()
  }

  // Driven by what is in the box rather than by the debounced copy, so the
  // hints get out of the way on the first keystroke instead of a beat later.
  const searching = query.trim() !== ''
  const total = hits.length

  const announcement = !searching
    ? ''
    : search.isPending
      ? 'Searching'
      : search.isError
        ? 'The search could not be run'
        : total === 1
          ? '1 message found'
          : `${total} messages found`

  return (
    <section className={cx(styles.panel, className)} aria-label="Search messages">
      <div className={styles.header}>
        <TextField
          ref={inputRef}
          label="Search messages"
          labelHidden
          type="search"
          value={query}
          placeholder="Search messages…"
          autoComplete="off"
          spellCheck={false}
          leading={<SearchIcon />}
          trailing={
            query === '' ? null : (
              <IconButton size="sm" label="Clear search" onClick={clearQuery}>
                <CloseIcon />
              </IconButton>
            )
          }
          onChange={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={onInputKeyDown}
        />

        {parsed.filters.length > 0 ? (
          <ul className={styles.chips} aria-label="Active filters">
            {parsed.filters.map((filter) => {
              const problem = filterProblem(filter)

              return (
                <li key={`${filter.kind}-${filter.start}`}>
                  <button
                    type="button"
                    className={cx(styles.chip, problem !== null && styles.chipInvalid)}
                    title={problem ?? undefined}
                    aria-label={`Remove filter ${filterLabel(filter)}`}
                    onClick={() => setQuery(removeSearchFilter(query, filter))}
                  >
                    <span className={styles.chipLabel}>{filterLabel(filter)}</span>
                    <span className={styles.chipRemove} aria-hidden="true">
                      <CloseIcon />
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}

        <div className={styles.quickFilters} aria-label="Quick filter shortcuts">
          <span className={styles.quickFilterLabel}>Quick filters:</span>
          {[
            { label: 'from:', token: 'from:', desc: 'Filter by sender' },
            { label: 'in:#', token: 'in:#', desc: 'Filter by channel' },
            { label: 'has:file', token: 'has:file', desc: 'Only with attachments' },
            { label: 'after:', token: 'after:', desc: 'After date YYYY-MM-DD' },
            { label: 'before:', token: 'before:', desc: 'Before date YYYY-MM-DD' },
          ].map((qf) => {
            const hasKind = parsed.filters.some((f) => f.kind === qf.token.replace(/[:#]/g, ''))
            return (
              <button
                key={qf.token}
                type="button"
                className={cx(styles.quickFilterBtn, hasKind && styles.quickFilterActive)}
                title={qf.desc}
                onClick={() => appendHint(qf.token)}
              >
                {qf.label}
              </button>
            )
          })}
        </div>
      </div>

      {/*
        Present from the first render rather than appearing with the results,
        because a live region that is added to the page at the same moment as
        its content is not reliably announced.
      */}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>

      <div className={styles.results}>
        {!searching ? (
          <div className={styles.hints}>
            <p className={styles.hintsTitle}>Narrow a search</p>
            <ul className={styles.hintList}>
              {HINTS.map((hint) => (
                <li key={hint.token}>
                  <button
                    type="button"
                    className={styles.hint}
                    onClick={() => appendHint(hint.token)}
                  >
                    <code className={styles.hintToken}>{hint.token}</code>
                    <span className={styles.hintText}>{hint.description}</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className={styles.hintsNote}>
              Tap a phrase to drop it into the field. They combine freely with ordinary words.
            </p>
          </div>
        ) : search.isPending ? (
          <div className={styles.centred}>
            <Spinner label="Searching" />
          </div>
        ) : search.isError ? (
          <EmptyState
            title="The search could not be run"
            description={search.error?.message ?? 'Try again in a moment.'}
            action={
              <Button variant="secondary" onClick={() => void search.refetch()}>
                Try again
              </Button>
            }
          />
        ) : total === 0 ? (
          <EmptyState
            title="No messages found"
            description="Try fewer words, or remove a filter."
          />
        ) : (
          <>
            <p className={styles.count}>
              {total === 1 ? '1 message' : `${total} messages`}
              {search.hasNextPage ? ' so far' : ''}
            </p>

            {positioned.map((group) => (
              <section key={group.room.id} className={styles.group}>
                <h3 className={styles.groupTitle}>
                  <span className={styles.groupName}>{roomLabel(group.room)}</span>
                  <span className={styles.groupCount}>{group.hits.length}</span>
                </h3>

                <ul className={styles.hitList}>
                  {group.hits.map(({ hit, index }) => (
                    <li key={hit.message.id}>
                      <SearchResult
                        hit={hit}
                        terms={terms}
                        onOpen={open}
                        onKeyDown={(event) => onResultKeyDown(event, index)}
                        registerRef={(node) => {
                          resultRefs.current[index] = node
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            {search.hasNextPage ? (
              <div className={styles.more}>
                <Button
                  variant="secondary"
                  loading={search.isFetchingNextPage}
                  onClick={() => void search.fetchNextPage()}
                >
                  Load more results
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  )
}

// ── One result ──────────────────────────────────────────────────────────────

interface SearchResultProps {
  hit: SearchHit
  terms: readonly string[]
  onOpen: (hit: SearchHit) => void
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void
  registerRef: (node: HTMLButtonElement | null) => void
}

function SearchResult({
  hit,
  terms,
  onOpen,
  onKeyDown,
  registerRef,
}: SearchResultProps): ReactElement {
  const { message } = hit
  const deleted = message.deleted_at !== null
  const name = senderName(message)
  const snippet = deleted
    ? { text: 'This message was deleted.', clippedStart: false, clippedEnd: false }
    : buildSnippet(message.text, terms)

  return (
    <button
      type="button"
      ref={registerRef}
      className={styles.hit}
      onClick={() => onOpen(hit)}
      onKeyDown={onKeyDown}
    >
      <Avatar
        name={name}
        id={message.sender.id}
        src={message.sender.avatar_url}
        size="xs"
        decorative
      />

      <span className={styles.hitBody}>
        <span className={styles.hitHead}>
          <span className={styles.hitSender}>{name}</span>
          <span className={styles.hitTime}>
            {formatDay(message.created_at)}
            <span aria-hidden="true"> · </span>
            {formatTime(message.created_at)}
          </span>
        </span>

        <span className={cx(styles.hitText, deleted && styles.hitDeleted)}>
          {snippet.clippedStart ? '…' : ''}
          {deleted ? snippet.text : highlight(snippet.text, terms)}
          {snippet.clippedEnd ? '…' : ''}
        </span>

        {/*
          A card rather than a list of names, so a hit on a file looks like the
          file it found. Spans, not links: the whole hit is already a button,
          and the file opens with the message it belongs to.
        */}
        {message.attachments.length > 0 ? (
          <span className={styles.hitFiles}>
            {message.attachments.map((attachment) => (
              <span key={attachment.id} className={styles.hitFile}>
                <span className={styles.hitFileIcon} aria-hidden="true">
                  <FileIcon />
                </span>
                <span className={styles.hitFileBody}>
                  <span className={styles.hitFileName}>{attachment.filename}</span>
                  <span className={styles.hitFileMeta}>
                    {formatFileKind(attachment.filename, attachment.mime_type)}
                    <span aria-hidden="true"> · </span>
                    {formatBytes(attachment.byte_size)}
                  </span>
                </span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </button>
  )
}
